<template>
  <b-card
    :title="title"
    :loading="loading"
    :error-message="errorMessage"
    :disabled="!formValid"
    @cancel="$emit('cancel')"
    :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
    :cancel-text="{ el: 'Ακύρωση', en: 'Cancel', it: 'Cancella' }"
    @submit="submitForm"
  >
    <b-select
      v-model="product.product_category_id"
      :items="categories"
      :item-text="`name[${lang}]`"
      item-value="product_category_id"
      :label="translations.selectCategory[lang]"
      no-top-margin
      :success="success.category"
      :error-messages="error.category"
      @focus="error.category = ''"
      @blur="validateCategory"
      :disabled="
        product.parent_product_id && mode !== 'create' && !superUser
          ? !getPermission('company_products.update_attribute.product_category')
          : false
      "
    ></b-select>

    <b-text-field
      v-model="product.name[productLang]"
      :label="translations.productName[lang]"
      :success="productLang === 'el' ? success.name : false"
      :error-messages="error.name"
      @focus="error.name = ''"
      @blur="validateName"
      :disabled="
        product.parent_product_id && mode !== 'create' && !superUser
          ? !getPermission('company_products.update_attribute.product_name')
          : false
      "
    >
      <template v-slot:append>
        <b-lang-menu v-model="productLang" type="inner"></b-lang-menu>
      </template>
    </b-text-field>
    <b-textarea
      v-model="product.description[descriptionLang]"
      :label="translations.productDescription[lang]"
      :success="descriptionLang === 'el' ? success.description : false"
      :error-messages="error.description"
      @focus="error.description = ''"
      @blur="validateDescription"
      :disabled="
        product.parent_product_id && mode !== 'create' && !superUser
          ? !getPermission('company_products.update_attribute.product_description')
          : false
      "
    >
      <template v-slot:append>
        <b-lang-menu v-model="descriptionLang" type="inner"></b-lang-menu>
      </template>
    </b-textarea>
    <b-text-field
      v-model="product.sku_number"
      :disabled="
        product.parent_product_id && mode !== 'create' && !superUser
          ? !getPermission('company_products.update_attribute.sku_number')
          : false
      "
      label="SKU"
    >
    </b-text-field>
    <v-row no-gutters>
      <v-col cols="12">
        <h4 class="secondary--text mt-3" v-text="translations.prices[lang]"></h4>
      </v-col>
      <v-col cols="6" class="pr-2">
        <b-text-field
          v-model="product.retail_price"
          :label="translations.sellingPrice[lang]"
          type="number"
          prepend-inner-icon="mdiCurrencyEur"
          :success="success.sellingPrice"
          :error-messages="error.sellingPrice"
          @focus="error.sellingPrice = ''"
          @blur="validateSellingPrice"
          :disabled="
            product.parent_product_id && mode !== 'create' && !superUser
              ? !getPermission('company_products.update_attribute.product_price')
              : false
          "
        ></b-text-field>
      </v-col>
      <v-col cols="6" class="pl-2">
        <b-text-field
          v-model="product.wholesale_price"
          :label="translations.wholesalePrice[lang]"
          type="number"
          prepend-inner-icon="mdiCurrencyEur"
          :success="success.wholesalePrice"
          :error-messages="error.wholesalePrice"
          @focus="error.wholesalePrice = ''"
          @blur="validateWholesalePrice"
          :disabled="
            product.parent_product_id && mode !== 'create' && !superUser
              ? !getPermission('company_products.update_attribute.product_price')
              : false
          "
        ></b-text-field>
      </v-col>
    </v-row>
    <v-col cols="6" class="pr-2 pl-0 pt-1">
      <b-text-field
        v-model="product.maximum_price_per_transaction"
        type="number"
        :label="translations.maxPricePerTransaction[lang]"
        prepend-inner-icon="mdiCurrencyEur"
        :success="success.maxPrice"
        :error-messages="error.maxPrice"
        @focus="error.maxPrice = ''"
        @blur="validateMaxPrice"
      ></b-text-field>
    </v-col>
    <v-checkbox
      v-model="showImageUpload"
      color="secondary"
      class="pt-0 mt-3"
      hide-details="auto"
      :disabled="
        product.parent_product_id && mode !== 'create' && !superUser
          ? !getPermission('company_products.update_attribute.product_image')
          : false
      "
    >
      <template v-slot:label>
        <h4 class="secondary--text" v-text="translations.imageCheckbox[lang]"></h4>
      </template>
    </v-checkbox>

    <v-card v-if="showImageUpload" outlined class="mt-3">
      <v-card-title
        class="subtitle-1 font-weight-medium"
        v-text="translations.imageTitle[lang]"
      ></v-card-title>
      <v-row no-gutters justify="space-between" class="pa-5">
        <v-col cols="6">
          <v-img :src="product.image"></v-img>
        </v-col>
        <v-col cols="5" class="mr-3">
          <v-file-input
            color="secondary"
            outlined
            dense
            hide-details="auto"
            @change="onFileSelected"
            :error-messages="error.image"
            :success="success.image"
            :disabled="
              product.parent_product_id && mode !== 'create' && !superUser
                ? !getPermission('company_products.update_attribute.product_image')
                : false
            "
          ></v-file-input>
        </v-col>
      </v-row>
    </v-card>

    <v-checkbox v-model="showWeekdays" color="secondary" class="pt-0 mt-3" hide-details="auto">
      <template v-slot:label>
        <h4 class="secondary--text" v-text="translations.daysCheckbox[lang]"></h4>
      </template>
    </v-checkbox>

    <v-card v-if="showWeekdays" outlined class="mt-3">
      <v-card-title
        class="subtitle-1 font-weight-medium"
        style="word-break: normal"
        v-text="translations.daysInfo[lang]"
      ></v-card-title>
      <v-container>
        <v-row no-gutters>
          <v-col v-for="(weekday, i) in weekdays" :key="weekday['en']" cols="3" class="pr-2">
            <v-checkbox
              v-model="product.availability_days"
              color="secondary"
              class="mt-0"
              :value="i"
              hide-details
              :disabled="
                product.parent_product_id && mode !== 'create' && !superUser
                  ? !getPermission('company_products.update_attribute.product_days')
                  : false
              "
            >
              <template v-slot:label>
                <h4 class="secondary--text" v-text="weekday[lang]"></h4>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <v-checkbox
      v-model="product.published"
      color="secondary"
      class="mt-3 pt-0"
      hide-details="auto"
      :disabled="
        product.parent_product_id && mode !== 'create' && !superUser
          ? !getPermission('company_products.update_attribute.product_published')
          : false
      "
    >
      <template v-slot:label>
        <h4 class="secondary--text" v-text="translations.published[lang]"></h4>
      </template>
    </v-checkbox>
  </b-card>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';

import translations from '@/utils/translations/storePanel/products/productForm';
import validators from '@/utils/validators/storePanel/product';
import { diff } from 'deep-object-diff';

export default {
  name: 'ProductForm',

  props: {
    mode: String,
    initialValues: {
      type: Object,
      default: () => ({}),
    },
  },

  mixins: [translations, validators],

  data() {
    return {
      productLang: this.$route.params.lang,
      descriptionLang: this.$route.params.lang,
      changeNameElAuto: true,
      changeNameItAuto: true,
      changeNameEnAuto: true,
      changeDescriptionElAuto: true,
      changeDescriptionItAuto: true,
      changeDescriptionEnAuto: true,
      imageFile: null,
      newValues: {},
      weekdays: [
        { el: 'Κυριακή', en: 'Sunday', it: 'Domenica' },
        { el: 'Δευτέρα', en: 'Monday', it: 'Lunedì' },
        { el: 'Τρίτη', en: 'Tuesday', it: 'Martedì' },
        { el: 'Τετάρτη', en: 'Wednesday', it: 'Mercoledì' },
        { el: 'Πέμπτη', en: 'Thursday', it: 'Giovedì' },
        { el: 'Παρασκευή', en: 'Friday', it: 'Venerdì' },
        { el: 'Σάββατο', en: 'Saturday', it: 'Sabato' },
      ],
    };
  },

  watch: {
    ['$route.params.lang'](val) {
      this.productLang = val;
      this.descriptionLang = val;
    },

    ['product.name.en'](val) {
      if (this.lang == 'en') {
        if (this.changeNameElAuto) {
          this.product.name.el = val;
        }

        if (this.changeNameItAuto) {
          this.product.name.it = val;
        }

        this.changeNameEnAuto = false;
      } else if (val != this.product.name[this.lang]) {
        this.changeNameEnAuto = false;
      }
    },

    ['product.name.el'](val) {
      if (this.lang == 'el') {
        if (this.changeNameEnAuto) {
          this.product.name.en = val;
        }

        if (this.changeNameItAuto) {
          this.product.name.it = val;
        }

        this.changeNameElAuto = false;
      } else if (val != this.product.name[this.lang]) {
        this.changeNameElAuto = false;
      }
    },

    ['product.name.it'](val) {
      if (this.lang == 'it') {
        if (this.changeNameElAuto) {
          this.product.name.el = val;
        }

        if (this.changeNameEnAuto) {
          this.product.name.en = val;
        }

        this.changeNameItAuto = false;
      } else if (val != this.product.name[this.lang]) {
        this.changeNameItAuto = false;
      }
    },

    ['product.description.en'](val) {
      if (this.lang == 'en') {
        if (this.changeDescriptionElAuto) {
          this.product.description.el = val;
        }

        if (this.changeDescriptionItAuto) {
          this.product.description.it = val;
        }

        this.changeDescriptionEnAuto = false;
      } else if (val != this.product.description[this.lang]) {
        this.changeDescriptionEnAuto = false;
      }
    },

    ['product.description.el'](val) {
      if (this.lang == 'el') {
        if (this.changeDescriptionEnAuto) {
          this.product.description.en = val;
        }

        if (this.changeDescriptionItAuto) {
          this.product.description.it = val;
        }

        this.changeDescriptionElAuto = false;
      } else if (val != this.product.description[this.lang]) {
        this.changeDescriptionElAuto = false;
      }
    },

    ['product.description.it'](val) {
      if (this.lang == 'it') {
        if (this.changeDescriptionElAuto) {
          this.product.description.el = val;
        }

        if (this.changeDescriptionEnAuto) {
          this.product.description.en = val;
        }

        this.changeDescriptionItAuto = false;
      } else if (val != this.product.description[this.lang]) {
        this.changeDescriptionItAuto = false;
      }
    },

    showWeekdays(val) {
      if (!val) {
        this.product.availability_days = [];
      }
    },

    product: {
      handler(val) {
        this.getNewValues();
      },
      deep: true,
    },
  },

  computed: {
    ...mapState([
      'loading',
      'errorMessage',
      'resetSuccess',
      'resetValidation',
      'permissions',
      'storePermissions',
      'superUser',
    ]),
    ...mapState('storePanel/products', ['categories', 'product']),

    lang() {
      return this.$route.params.lang;
    },

    title() {
      return this.mode === 'create'
        ? this.translations.createTitle[this.lang]
        : this.translations.updateTitle[this.lang];
    },

    showImageUpload: {
      get() {
        return this.$store.state.storePanel.products.showImageUpload;
      },

      set(val) {
        this.setShowImageUpload(val);
      },
    },

    showWeekdays: {
      get() {
        return this.$store.state.storePanel.products.showWeekdays;
      },

      set(val) {
        this.setShowWeekdays(val);
      },
    },
  },

  methods: {
    ...mapMutations(['setResetSuccess', 'setResetValidation']),
    ...mapMutations('storePanel/products', ['setShowImageUpload', 'setShowWeekdays']),
    ...mapActions('storePanel/products', ['getCategories', 'create', 'update']),

    getPermission(path) {
      return eval('this.permissions.' + path) && eval('this.storePermissions.' + path);
    },
    onFileSelected(event) {
      if (!event) return;
      const file = Array.isArray(event) ? event[0] : event;

      const validImg = this.validateImage ? this.validateImage(file) : true;
      if (!validImg) return;

      this.imageFile = file;
      const reader = new FileReader();
      reader.readAsDataURL(this.imageFile);
      reader.onload = (e) => (this.product.image = e.target.result);
    },
    getNewValues() {
      let newVals = JSON.parse(JSON.stringify(this.product));
      this.newValues = { ...diff(this.initialValues, newVals) };
      for (let key of Object.keys(newVals)) {
        if (Array.isArray(newVals[key])) {
          if (
            newVals[key].sort().toString() !== (this.initialValues[key] || []).sort().toString()
          ) {
            this.newValues[key] = [...newVals[key]];
          } else {
            delete this.newValues[key];
          }
        }
      }
    },
    submitForm() {
      if (this.validateImage && !this.validateImage(this.imageFile)) {
        return;
      }

      if (this.mode === 'create') {
        this.create(this.imageFile);
      } else {
        this.update({ newValues: this.newValues, image: this.imageFile });
      }
    },
  },

  mounted() {
    this.getCategories();
  },
};
</script>
